import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { Crosshair, Target, Check } from 'lucide-react'
import { disciplines } from '../../data/siteData'

export default function Disciplines() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section id="disciplines" className="relative py-24 sm:py-32 bg-section-dark grain-overlay" style={{ backgroundColor: 'rgba(10,14,10,0.55)', backdropFilter: 'blur(2px)' }}>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-military/40 to-transparent" />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-20"
        >
          <span className="font-mono text-xs text-military-light tracking-[0.4em] uppercase">
            Dyscypliny
          </span>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl text-bone mt-4 tracking-wider">
            CO TO JEST?
          </h2>
          <p className="font-body text-chrome mt-4 max-w-2xl mx-auto">
            Wybierz swoją broń. Każda dyscyplina to inny świat adrenaliny.
          </p>
          <div className="w-24 h-0.5 bg-military-light mx-auto mt-6" />
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {disciplines.map((discipline, i) => {
            const Icon = discipline.id === 'asg' ? Crosshair : Target
            const isASG = discipline.id === 'asg'

            return (
              <motion.div
                key={discipline.id}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: i * 0.2 }}
                className={`group relative p-8 sm:p-10 bg-[#111111]/70 border rounded-sm overflow-hidden transition-all duration-500 hover:-translate-y-1 ${
                  isASG 
                    ? 'border-military/30 hover:border-military/60 hover:shadow-lg hover:shadow-military/10' 
                    : 'border-amber/20 hover:border-amber/50 hover:shadow-lg hover:shadow-amber/10'
                }`}
              >
                {/* Background Accent */}
                <div className={`absolute top-0 left-0 right-0 h-1 ${
                  isASG ? 'bg-military' : 'bg-amber'
                }`} />

                {/* Icon */}
                <div className={`w-16 h-16 flex items-center justify-center rounded-sm mb-6 ${
                  isASG ? 'bg-military/10 border border-military/30' : 'bg-amber/10 border border-amber/30'
                }`}>
                  <Icon size={32} className={isASG ? 'text-military-light' : 'text-amber'} />
                </div>

                {/* Title */}
                <h3 className="font-display text-3xl sm:text-4xl text-bone tracking-wider mb-4">
                  {discipline.title}
                </h3>

                {/* Description */}
                <p className="font-body text-chrome-light leading-relaxed mb-8">
                  {discipline.description}
                </p>

                {/* Features */}
                <ul className="space-y-3">
                  {discipline.features.map((feature, fi) => (
                    <li key={fi} className="flex items-center gap-3">
                      <div className={`w-5 h-5 flex items-center justify-center rounded-full ${
                        isASG ? 'bg-military/20' : 'bg-amber/20'
                      }`}>
                        <Check size={12} className={isASG ? 'text-military-light' : 'text-amber'} />
                      </div>
                      <span className="font-body text-sm text-chrome-light">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Hover Glow */}
                <div className={`absolute -bottom-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 ${
                  isASG ? 'bg-military-light' : 'bg-amber'
                }`} />
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
